<template>
    <div class="vehicles">
        <div class="flex dashboard-wrapper">
            <div class="w-[15%] bg-slate-100">
                <SidebarDashboard/>
            </div>

            <div class="w-[85%]">
                <Header></Header>

                <div class="dashboard-content p-5 px-10 pb-20 min-h-screen bg-[#f5f5f5]">
                    <!-- Page Title -->
                    <div class="flex items-center justify-between mb-6">
                        <div>
                            <h2 class="font-bold text-3xl text-gray-800">{{ currentTitle }}</h2>
                            <p class="text-gray-500 text-sm mt-1">Visão geral do negócio em tempo real</p>
                        </div>
                        <div class="text-sm text-gray-400 flex items-center gap-2">
                            <i class="pi pi-refresh cursor-pointer hover:text-blue-600 transition" @click="loadAllData"></i>
                            Atualizado agora
                        </div>
                    </div>

                    <!-- KPI Cards -->
                    <div v-if="loadingKpis" class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div v-for="n in 4" :key="n" class="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                            <Skeleton height="1rem" class="mb-3" />
                            <Skeleton height="2.5rem" class="mb-2" />
                            <Skeleton height="0.8rem" width="60%" />
                        </div>
                    </div>

                    <div v-else class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <!-- Total de Clientes -->
                        <div class="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow group">
                            <div class="flex items-center justify-between mb-3">
                                <p class="text-sm font-medium text-gray-500">Total de Clientes</p>
                                <div class="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition">
                                    <i class="pi pi-users text-blue-600"></i>
                                </div>
                            </div>
                            <span class="block text-4xl font-bold text-gray-800">{{ stats.customers }}</span>
                            <p class="text-xs text-green-600 mt-1 flex items-center gap-1">
                                <i class="pi pi-arrow-up text-[10px]"></i> Clientes cadastrados
                            </p>
                        </div>

                        <!-- Total de Assinaturas -->
                        <div class="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow group">
                            <div class="flex items-center justify-between mb-3">
                                <p class="text-sm font-medium text-gray-500">Assinaturas</p>
                                <div class="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center group-hover:bg-purple-100 transition">
                                    <i class="pi pi-file text-purple-600"></i>
                                </div>
                            </div>
                            <span class="block text-4xl font-bold text-gray-800">{{ stats.hirings }}</span>
                            <div class="flex gap-3 mt-1">
                                <p class="text-xs text-green-600 flex items-center gap-1">
                                    <span class="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                                    {{ stats.hirings_active }} ativas
                                </p>
                                <p class="text-xs text-red-400 flex items-center gap-1">
                                    <span class="w-2 h-2 rounded-full bg-red-400 inline-block"></span>
                                    {{ stats.hirings_expired }} expiradas
                                </p>
                            </div>
                        </div>

                        <!-- Total de Veículos -->
                        <div class="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow group">
                            <div class="flex items-center justify-between mb-3">
                                <p class="text-sm font-medium text-gray-500">Veículos Cadastrados</p>
                                <div class="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center group-hover:bg-green-100 transition">
                                    <i class="pi pi-car text-green-600"></i>
                                </div>
                            </div>
                            <span class="block text-4xl font-bold text-gray-800">{{ stats.customer_vehicles }}</span>
                            <p class="text-xs text-gray-400 mt-1">Todos os veículos monitorados</p>
                        </div>

                        <!-- Inadimplentes -->
                        <div class="bg-white rounded-xl p-5 shadow-sm border border-red-100 hover:shadow-md transition-shadow group">
                            <div class="flex items-center justify-between mb-3">
                                <p class="text-sm font-medium text-gray-500">Inadimplentes</p>
                                <div class="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition">
                                    <i class="pi pi-exclamation-triangle text-red-500"></i>
                                </div>
                            </div>
                            <span class="block text-4xl font-bold text-red-600">{{ delinquents.length }}</span>
                            <p class="text-xs text-red-400 mt-1 flex items-center gap-1">
                                <i class="pi pi-money-bill text-[10px]"></i>
                                {{ formatCurrency(totalDelinquentValue) }} pendentes
                            </p>
                        </div>
                    </div>

                    <!-- Sales Chart with period filters -->
                    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
                            <div>
                                <h3 class="font-semibold text-gray-800 text-lg">Vendas por Período</h3>
                                <p class="text-gray-400 text-sm">Volume de vendas filtrado pelo período selecionado</p>
                            </div>
                            <div class="flex flex-wrap gap-2">
                                <button
                                    v-for="period in periodOptions"
                                    :key="period.value"
                                    @click="selectPeriod(period.value)"
                                    :class="[
                                        'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all',
                                        selectedPeriod === period.value
                                            ? 'bg-blue-600 text-white shadow-md'
                                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                    ]">
                                    {{ period.label }}
                                </button>
                            </div>
                        </div>

                        <!-- Sales Summary Row -->
                        <div class="grid grid-cols-3 gap-4 mb-5 p-4 bg-gray-50 rounded-lg">
                            <div class="text-center">
                                <p class="text-xs text-gray-500 mb-1">Total em Vendas</p>
                                <p class="text-xl font-bold text-blue-600">{{ formatCurrency(salesSummary.total) }}</p>
                            </div>
                            <div class="text-center border-x border-gray-200">
                                <p class="text-xs text-gray-500 mb-1">Pedidos Finalizados</p>
                                <p class="text-xl font-bold text-green-600">{{ salesSummary.count }}</p>
                            </div>
                            <div class="text-center">
                                <p class="text-xs text-gray-500 mb-1">Ticket Médio</p>
                                <p class="text-xl font-bold text-purple-600">{{ formatCurrency(salesSummary.average) }}</p>
                            </div>
                        </div>

                        <div v-if="loadingOrders">
                            <Skeleton height="250px" />
                        </div>
                        <Chart v-else-if="salesChartData && salesChartData.labels && salesChartData.labels.length > 0"
                            type="bar"
                            :data="salesChartData"
                            :options="salesChartOptions"
                            style="height: 280px;" />
                        <div v-else class="flex flex-col items-center justify-center h-[200px] text-gray-400">
                            <i class="pi pi-chart-bar text-4xl mb-3"></i>
                            <p class="text-sm">Sem dados de vendas para o período selecionado</p>
                        </div>
                    </div>

                    <!-- Charts Row: Doughnut + Bar -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <!-- Payment Methods Chart -->
                        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                            <h3 class="font-semibold text-gray-800 text-lg mb-1">Métodos de Pagamento</h3>
                            <p class="text-gray-400 text-sm mb-5">Distribuição por forma de pagamento</p>
                            <div v-if="loadingOrders">
                                <Skeleton height="220px" />
                            </div>
                            <Chart v-else-if="paymentMethodChartData"
                                type="doughnut"
                                :data="paymentMethodChartData"
                                :options="doughnutChartOptions"
                                style="height: 220px;" />
                            <div v-else class="flex flex-col items-center justify-center h-[160px] text-gray-400">
                                <i class="pi pi-chart-pie text-4xl mb-3"></i>
                                <p class="text-sm">Sem dados disponíveis</p>
                            </div>
                        </div>

                        <!-- Subscription Status Chart -->
                        <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                            <h3 class="font-semibold text-gray-800 text-lg mb-1">Status das Assinaturas</h3>
                            <p class="text-gray-400 text-sm mb-5">Ativas vs expiradas no total</p>
                            <div v-if="loadingReports">
                                <Skeleton height="220px" />
                            </div>
                            <Chart v-else-if="subscriptionStatusChartData"
                                type="pie"
                                :data="subscriptionStatusChartData"
                                :options="doughnutChartOptions"
                                style="height: 220px;" />
                            <div v-else class="flex flex-col items-center justify-center h-[160px] text-gray-400">
                                <i class="pi pi-chart-pie text-4xl mb-3"></i>
                                <p class="text-sm">Sem dados disponíveis</p>
                            </div>
                        </div>
                    </div>

                    <!-- Plans Chart -->
                    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                        <h3 class="font-semibold text-gray-800 text-lg mb-1">Planos Mais Contratados</h3>
                        <p class="text-gray-400 text-sm mb-5">Ranking de planos pelo total de assinaturas</p>
                        <div v-if="loadingReports">
                            <Skeleton height="200px" />
                        </div>
                        <Chart v-else-if="plansChartData"
                            type="bar"
                            :data="plansChartData"
                            :options="plansChartOptions"
                            style="height: 220px;" />
                        <div v-else class="flex flex-col items-center justify-center h-[160px] text-gray-400">
                            <i class="pi pi-chart-bar text-4xl mb-3"></i>
                            <p class="text-sm">Sem dados disponíveis</p>
                        </div>
                    </div>

                    <!-- Delinquent Customers Section -->
                    <div class="bg-white rounded-xl shadow-sm border border-red-100 p-6 mb-6">
                        <div class="flex items-center gap-3 mb-1">
                            <div class="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
                                <i class="pi pi-exclamation-triangle text-red-500 text-sm"></i>
                            </div>
                            <h3 class="font-semibold text-gray-800 text-lg">Clientes Inadimplentes</h3>
                        </div>
                        <p class="text-gray-400 text-sm mb-5 ml-11">Pedidos com pagamento pendente ou cancelado</p>

                        <div v-if="loadingOrders">
                            <Skeleton height="200px" />
                        </div>
                        <div v-else-if="delinquents.length === 0" class="flex flex-col items-center justify-center py-12 text-gray-400">
                            <i class="pi pi-check-circle text-5xl mb-3 text-green-400"></i>
                            <p class="font-medium text-green-500">Nenhum cliente inadimplente encontrado!</p>
                            <p class="text-sm mt-1">Todos os pagamentos estão em dia.</p>
                        </div>
                        <TableDelinquents v-else :delinquents="delinquents" />
                    </div>

                    <!-- Full Report Table -->
                    <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h3 class="font-semibold text-gray-800 text-lg mb-1">Relatório de Contratações</h3>
                        <p class="text-gray-400 text-sm mb-5">Todas as contratações com status de vigência</p>
                        <TableReport />
                    </div>
                </div>

                <Footer></Footer>
            </div>
        </div>
    </div>
</template>

<script>
import SidebarDashboard from '@/components/Dashboard/SidebarDashboard.vue'
import Header from '@/components/Dashboard/HeaderDashboard.vue'
import Footer from '@/components/Dashboard/FooterDashboard.vue'
import Chart from 'primevue/chart'
import TableReport from '@/components/Dashboard/TableReport.vue'
import TableDelinquents from '@/components/Dashboard/TableDelinquents.vue'
import initService from '@/services/initService'
import orderService from '@/services/orderService'
import requestService from '@/services/requestService'

export default {
    name: 'DashboardReportings',
    components: {
        SidebarDashboard, Header, Footer, Chart, TableReport, TableDelinquents
    },
    data() {
        return {
            currentTitle: '',

            // Loading states
            loadingKpis: true,
            loadingOrders: true,
            loadingReports: true,

            // Stats
            stats: {
                customers: 0,
                hirings: 0,
                hirings_month: 0,
                customer_vehicles: 0,
                hirings_active: 0,
                hirings_expired: 0,
            },

            // Orders raw data
            allOrders: [],

            // Reports (hirings) raw data
            allReports: [],

            // Delinquents
            delinquents: [],
            totalDelinquentValue: 0,

            // Period filter
            selectedPeriod: 30,
            periodOptions: [
                { label: '7 dias', value: 7 },
                { label: '30 dias', value: 30 },
                { label: '90 dias', value: 90 },
                { label: 'Este Ano', value: 365 },
                { label: 'Tudo', value: 9999 },
            ],

            // Charts data
            salesChartData: null,
            salesChartOptions: null,
            paymentMethodChartData: null,
            doughnutChartOptions: null,
            subscriptionStatusChartData: null,
            plansChartData: null,
            plansChartOptions: null,

            // Sales summary
            salesSummary: {
                total: 0,
                count: 0,
                average: 0,
            },
        }
    },

    methods: {
        async loadAllData() {
            this.loadingKpis = true
            this.loadingOrders = true
            this.loadingReports = true
            await Promise.all([
                this.loadKpis(),
                this.loadOrders(),
                this.loadReports(),
            ])
        },

        async loadKpis() {
            try {
                const response = await initService.getAllNumbers()
                if (response && response.data) {
                    this.stats.customers = response.data.customers || 0
                    this.stats.hirings = response.data.hirings || 0
                    this.stats.hirings_month = response.data.hirings_month || 0
                    this.stats.customer_vehicles = response.data.customer_vehicles || 0
                }
            } catch (e) {
                console.error('Erro ao carregar KPIs:', e)
            } finally {
                this.loadingKpis = false
            }
        },

        async loadOrders() {
            try {
                const response = await orderService.getAll()
                if (response && response.data) {
                    this.allOrders = response.data
                    this.computeDelinquents()
                    this.computeSalesChart()
                    this.computePaymentMethodChart()
                }
            } catch (e) {
                console.error('Erro ao carregar pedidos:', e)
            } finally {
                this.loadingOrders = false
            }
        },

        async loadReports() {
            try {
                const response = await requestService.get('reports')
                if (response && response.data) {
                    this.allReports = response.data
                    this.computeSubscriptionStatusChart()
                    this.computePlansChart()
                    this.computeHiringsStats()
                }
            } catch (e) {
                console.error('Erro ao carregar relatórios:', e)
            } finally {
                this.loadingReports = false
            }
        },

        computeHiringsStats() {
            let active = 0
            let expired = 0
            this.allReports.forEach(r => {
                if (r.hiring_status === 1 || r.hiring_status === '1') active++
                else expired++
            })
            this.stats.hirings_active = active
            this.stats.hirings_expired = expired
        },

        computeDelinquents() {
            // Group orders by customer where payment is 'waiting' or 'canceled' but not 'paid'
            const delinquentMap = {}
            this.allOrders.forEach(order => {
                const payStatus = order.payment_status
                const orderStatus = order.order_status
                const isPendingPayment = payStatus === 'waiting' || (orderStatus === 'waiting' && !payStatus)
                if (!isPendingPayment) return

                const cid = order.customer_id
                if (!delinquentMap[cid]) {
                    delinquentMap[cid] = {
                        customer_id: cid,
                        name: order.name || '-',
                        document: order.document || '-',
                        pending_count: 0,
                        pending_total: 0,
                        oldest_date: order.created_at,
                        payment_methods: [],
                    }
                }
                const entry = delinquentMap[cid]
                entry.pending_count++
                entry.pending_total += parseFloat(order.payment_total || order.order_total || 0)

                // Oldest date
                if (order.created_at && new Date(order.created_at) < new Date(entry.oldest_date)) {
                    entry.oldest_date = order.created_at
                }

                // Payment methods (unique)
                const method = order.payment_method
                if (method && !entry.payment_methods.includes(method)) {
                    entry.payment_methods.push(method)
                }
            })

            this.delinquents = Object.values(delinquentMap)
            this.totalDelinquentValue = this.delinquents.reduce((sum, d) => sum + d.pending_total, 0)
        },

        getFilteredOrders() {
            if (this.selectedPeriod === 9999) return this.allOrders

            const cutoff = new Date()
            cutoff.setDate(cutoff.getDate() - this.selectedPeriod)

            return this.allOrders.filter(order => {
                const date = new Date(order.created_at)
                return date >= cutoff
            })
        },

        computeSalesChart() {
            const filtered = this.getFilteredOrders().filter(o => o.order_status === 'finished' || o.payment_status === 'paid')

            // Group by date
            const grouped = {}
            filtered.forEach(order => {
                const dateObj = new Date(order.created_at)
                let label = ''

                if (this.selectedPeriod <= 30) {
                    // Group by day: DD/MM
                    label = dateObj.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                } else {
                    // Group by month: MM/YYYY
                    label = dateObj.toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' })
                }

                if (!grouped[label]) grouped[label] = { label, total: 0, count: 0 }
                grouped[label].total += parseFloat(order.payment_total || order.order_total || 0)
                grouped[label].count++
            })

            const sortedKeys = Object.keys(grouped).sort((a, b) => {
                const parseLabel = (l) => {
                    const parts = l.split('/')
                    if (parts.length === 2) {
                        const now = new Date()
                        return new Date(now.getFullYear(), parseInt(parts[1]) - 1, parseInt(parts[0]))
                    }
                    return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`)
                }
                return parseLabel(a) - parseLabel(b)
            })

            const labels = sortedKeys
            const values = sortedKeys.map(k => grouped[k].total)
            const counts = sortedKeys.map(k => grouped[k].count)

            const total = values.reduce((a, b) => a + b, 0)
            const count = counts.reduce((a, b) => a + b, 0)
            this.salesSummary = {
                total,
                count,
                average: count > 0 ? total / count : 0,
            }

            this.salesChartData = {
                labels,
                datasets: [
                    {
                        label: 'Vendas (R$)',
                        data: values,
                        backgroundColor: 'rgba(37, 99, 235, 0.15)',
                        borderColor: 'rgba(37, 99, 235, 0.8)',
                        borderWidth: 2,
                        borderRadius: 6,
                        tension: 0.4,
                        fill: true,
                    },
                ],
            }

            this.salesChartOptions = {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => {
                                return ` R$ ${ctx.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: { color: '#94a3b8', font: { size: 11 } }
                    },
                    y: {
                        grid: { color: 'rgba(0,0,0,0.04)' },
                        ticks: {
                            color: '#94a3b8',
                            font: { size: 11 },
                            callback: (val) => `R$ ${val.toLocaleString('pt-BR')}`
                        }
                    }
                }
            }
        },

        computePaymentMethodChart() {
            const methodMap = {}
            const labels = { pix: 'Pix', credit_card: 'Cartão de Crédito', ticket: 'Boleto' }
            const colors = ['#2563eb', '#7c3aed', '#059669']

            this.allOrders.forEach(order => {
                const method = order.payment_method
                if (!method) return
                if (!methodMap[method]) methodMap[method] = 0
                methodMap[method]++
            })

            const keys = Object.keys(methodMap)
            if (keys.length === 0) { this.paymentMethodChartData = null; return }

            this.paymentMethodChartData = {
                labels: keys.map(k => labels[k] || k),
                datasets: [{
                    data: keys.map(k => methodMap[k]),
                    backgroundColor: keys.map((_, i) => colors[i % colors.length]),
                    hoverOffset: 8,
                    borderWidth: 2,
                    borderColor: '#fff',
                }]
            }

            this.doughnutChartOptions = {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'bottom',
                        labels: { usePointStyle: true, padding: 20, color: '#64748b', font: { size: 12 } }
                    }
                }
            }
        },

        computeSubscriptionStatusChart() {
            let active = 0, expired = 0
            this.allReports.forEach(r => {
                if (r.hiring_status === 1 || r.hiring_status === '1') active++
                else expired++
            })

            if (active + expired === 0) { this.subscriptionStatusChartData = null; return }

            this.subscriptionStatusChartData = {
                labels: ['Ativas', 'Expiradas'],
                datasets: [{
                    data: [active, expired],
                    backgroundColor: ['#059669', '#ef4444'],
                    hoverOffset: 8,
                    borderWidth: 2,
                    borderColor: '#fff',
                }]
            }
        },

        computePlansChart() {
            const planMap = {}
            this.allReports.forEach(r => {
                const name = r.plan_name
                if (!name) return
                if (!planMap[name]) planMap[name] = 0
                planMap[name]++
            })

            const sorted = Object.entries(planMap).sort((a, b) => b[1] - a[1]).slice(0, 8)
            if (sorted.length === 0) { this.plansChartData = null; return }

            const gradients = [
                '#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626',
                '#0891b2', '#4f46e5', '#c026d3'
            ]

            this.plansChartData = {
                labels: sorted.map(([name]) => name),
                datasets: [{
                    label: 'Contratações',
                    data: sorted.map(([, count]) => count),
                    backgroundColor: sorted.map((_, i) => gradients[i % gradients.length]),
                    borderRadius: 6,
                    borderWidth: 0,
                }]
            }

            this.plansChartOptions = {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => ` ${ctx.parsed.x} contratações`
                        }
                    }
                },
                scales: {
                    x: {
                        grid: { color: 'rgba(0,0,0,0.04)' },
                        ticks: { color: '#94a3b8', font: { size: 11 }, stepSize: 1 }
                    },
                    y: {
                        grid: { display: false },
                        ticks: { color: '#475569', font: { size: 12 } }
                    }
                }
            }
        },

        selectPeriod(value) {
            this.selectedPeriod = value
            this.computeSalesChart()
        },

        formatCurrency(value) {
            if (!value && value !== 0) return 'R$ 0,00'
            return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        },
    },

    mounted() {
        this.currentTitle = this.$route.meta.title
        this.$watch('$route', () => {
            this.currentTitle = this.$route.meta.title
        })
        this.loadAllData()
    },
}
</script>

<style lang="scss" scoped>
.dashboard-wrapper {
    min-height: 100vh;
}
</style>